import { useSortable } from '@dnd-kit/sortable';
import { Box, TableCell as MuiTableCell, Stack, SxProps, Theme } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { Reorder, ChevronUp, ChevronDown } from '@sweep-io/sweep-design/dist/icons';
import { TruncatedTextTooltip } from '../TruncatedTextTooltip';
import { SortOrder } from '../types';
import { dataTableVariants } from './dataTableVariants';
import { SELECTION_COLUMN_FIELD } from './utils';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableSortableColumn,
  DataTableSortStateProps,
  DataTableVariant,
} from './TableTypes';
import { useRef } from 'react';
import { cellChevronStyles, getTableDividerCss, stickyHeaderStyles } from './utils';
import { CELL_MIN_WIDTH, ResizeHandle } from './custom-columns-table/ResizeHandle';
import { useCustomColumnsTableData } from './custom-columns-table/CustomColumnsTableContext';

interface DraggableTableHeaderProps<TRow extends DataTableRow> {
  column: DataTableBaseColumn | DataTableSortableColumn<TRow>;
  stickyHeader?: boolean;
  variant: DataTableVariant;
  sortState?: DataTableSortStateProps;
  sort?: (columnName: string) => void;
  isDragging?: boolean;
  isOver?: boolean;
  isOverlay?: boolean;
  isLastColumn?: boolean;
  sx?: SxProps<Theme>;
  overlayWidth?: number;
  isColumnResizeAllowed?: boolean;
}

export function DraggableTableHeadColumn<TRow extends DataTableRow = any>({
  column,
  stickyHeader,
  variant,
  sortState,
  sort,
  isDragging,
  isOver,
  isOverlay = false,
  isLastColumn,
  sx,
  overlayWidth,
  isColumnResizeAllowed,
}: DraggableTableHeaderProps<TRow>) {
  const isSelectionColumn = column.field === SELECTION_COLUMN_FIELD;
  const { attributes, listeners, setNodeRef } = useSortable({
    id: column.field,
    disabled: isSelectionColumn, // Disable dragging if column is selection column
  });

  const cellRef = useRef<HTMLTableCellElement | null>(null);
  const { columnWidths, resizingColumnId } = useCustomColumnsTableData();

  const isAnyColumnResizing = !!resizingColumnId;

  const isSortCompare = 'sortCompare' in column && column.sortCompare;
  const isShowSortIcon = 'showSortIcon' in column && column.showSortIcon;
  const isSortable = isSortCompare || isShowSortIcon;

  const columnWidth = columnWidths?.[column.field] ? columnWidths[column.field] + 'px' : undefined;
  const _columnWidth = columnWidth ?? column.width;
  const minWidth = columnWidth ? CELL_MIN_WIDTH : column.minWidth;

  const cellSx: SxProps<Theme> = {
    position: 'relative',
    ...(stickyHeader ? { ...stickyHeaderStyles } : {}),
    width: isOverlay && overlayWidth ? overlayWidth : _columnWidth,
    maxWidth: isOverlay && overlayWidth ? overlayWidth : _columnWidth,
    minWidth,
    padding: dataTableVariants[variant].cellPadding,
    borderBottom: `1px solid ${colors.grey[120]}`,
    borderRight: isLastColumn || isDragging ? 'none' : `1px solid transparent`,
    cursor: isAnyColumnResizing ? 'col-resize' : undefined,
    overflow: 'hidden',
    '&:active': {
      cursor: isAnyColumnResizing ? 'col-resize' : !isSelectionColumn ? 'grabbing' : 'default',
    },
    '&:hover .drag-handle': {
      opacity: !isSelectionColumn ? 1 : 0,
      visibility: !isSelectionColumn ? 'visible' : 'hidden',
    },
    ...(isOver &&
      !isDragging && {
        borderRight: `1px solid ${colors.blue[500]}`,
      }),
    '&:hover': {
      backgroundColor: isAnyColumnResizing ? undefined : colors.grey[150],
    },
    ...(!isLastColumn && getTableDividerCss(!!isOver, !!isDragging)),
    ...sx,
  } as SxProps<Theme>;

  const renderColumnContent = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
        minWidth: 0,
        '& > .chevron-wrapper': {
          opacity: sortState?.sortBy === column.field ? 1 : 0,
          marginLeft: 1,
        },
        '&:hover .chevron-wrapper': {
          opacity: isAnyColumnResizing ? 0 : 1,
          transition: '.5s',
        },
      }}
    >
      {!isSelectionColumn && (
        <Stack
          className="drag-handle"
          sx={{
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            alignItems: 'center',
            position: 'absolute',
            left: '7px',
            visibility: 'hidden',
            opacity: 0,
            cursor: 'grab',
          }}
          {...listeners}
        >
          <Reorder size="small" color={colors.grey[800]} />
        </Stack>
      )}

      <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
        {isSelectionColumn ? (
          // No tooltip for selection column (it's a checkbox, not text)
          <div>{column.headerName}</div>
        ) : (
          <TruncatedTextTooltip variant="body" color={colors.grey[800]}>
            {column.headerName}
          </TruncatedTextTooltip>
        )}

        {isShowSortIcon && !isAnyColumnResizing && (
          <Box
            data-testid="data-table-sort-icon"
            width="16px"
            height="16px"
            className="chevron-wrapper"
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer', opacity: 0 }}
          >
            {sortState?.sortBy === column.field && sortState?.sortOrder === SortOrder.ASC ? (
              <ChevronDown size="small" color={colors.blue[500]} />
            ) : (
              <ChevronUp size="small" color={colors.blue[500]} />
            )}
          </Box>
        )}
        {(!isShowSortIcon || isAnyColumnResizing) && <Box width="24px" />}
      </Stack>
      {isColumnResizeAllowed && <ResizeHandle column={column} cellRef={cellRef} />}
    </Box>
  );

  if (isDragging && !isOverlay) {
    return (
      <MuiTableCell
        onClick={() => isSortable && sort && sort(column.field)}
        ref={(element: HTMLTableCellElement | null) => {
          setNodeRef(element);
          cellRef.current = element;
        }}
        {...attributes}
        data-column-id={column.field}
        sx={{
          ...cellSx,
          pointerEvents: 'none',
          ...cellChevronStyles,
        }}
        className={column.className}
      >
        {renderColumnContent()}
      </MuiTableCell>
    );
  }

  return (
    <MuiTableCell
      onClick={() => isSortable && sort && sort(column.field)}
      ref={(element: HTMLTableCellElement | null) => {
        setNodeRef(element);
        cellRef.current = element;
      }}
      {...attributes}
      data-column-id={column.field}
      sx={{ ...cellSx, ...cellChevronStyles }}
      className={column.className}
    >
      {renderColumnContent()}
    </MuiTableCell>
  );
}
